import Heading from 'molecules/Heading';
import Pagination from 'molecules/Pagination';
import Section from 'molecules/Section';

import ScrollFadeSection from 'components/Animations/ScrollFadeSection';

import ResourceCard from 'layout/Listing/components/ResourceCard';
import usePagination from 'layout/Listing/hooks/usePagination';

import { hasArrayValues } from 'utils/array/arrays';

import type { FC } from 'react';

interface AuthorFeedProps {
  /** Anchor for the section, and the scroll target when the page changes. */
  id: string;
  heading: string;
  posts: { data: BlogResource }[];
  /**
   * Cards per page. Omit to render every card without pagination: the
   * "Further reading" deck is a short hand-picked list.
   */
  pageSize?: number;
}

const AuthorFeed: FC<AuthorFeedProps> = ({ id, heading, posts, pageSize }) => {
  // The hook reads `?page=` from the URL, so it can only drive the one
  // paginated deck on the page; an unpaginated deck must ignore its slice or a
  // visitor on page 2 would see it empty.
  const { currentPage, paginatedItems, handlePageChange } = usePagination({
    items: posts,
    pageSize: pageSize || Math.max(posts.length, 1),
    scrollTargetId: id,
  });
  const visiblePosts = pageSize ? paginatedItems : posts;

  if (!hasArrayValues(posts)) {
    return null;
  }

  return (
    <Section noDarkMode id={id}>
      <div className="flex w-full flex-col items-center justify-center gap-8 sm:gap-12 lg:gap-16">
        <Heading heading={heading} alignment="center" />
        <ScrollFadeSection delay={0.2} className="w-full">
          <div className="grid w-full grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {visiblePosts.map((post, index) => (
              <ResourceCard key={post.data.url || post.data.title} {...post.data} priority={index === 0} />
            ))}
          </div>
        </ScrollFadeSection>
        {!!pageSize && (
          <Pagination
            count={posts.length}
            pageSize={pageSize}
            currentPage={currentPage}
            setPage={details => handlePageChange(details.page)}
          />
        )}
      </div>
    </Section>
  );
};

export default AuthorFeed;
